import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getUserLanguagePreference, isValidLanguage } from '../utils/languageUtils';
import { captureException } from '../utils/captureException';

interface LanguageContextType {
  currentLanguage: string;
  changeLanguage: (language: string) => void;
  availableLanguages: { code: string; name: string; nativeName: string }[];
  isUpdatingLanguage: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
  user?: any; // Firebase user
  userData?: any; // User data from Firestore
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children, user, userData }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'es');
  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);

  const availableLanguages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'es', name: 'Spanish', nativeName: 'Español' },
  ];

  // Function to sync language to Firestore when user is authenticated
  const syncLanguageToFirestore = async (language: string) => {
    if (!user || !userData?.onboardingCompleted) {
      return; // Don't sync if user is not authenticated or hasn't completed onboarding
    }

    try {
      setIsUpdatingLanguage(true);
      const idToken = await user.getIdToken();
      
      // Update language in Firestore via settings endpoint
      await axios.put(
        `${process.env.REACT_APP_API_URL || ''}/api/user/settings`,
        {
          language: language,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
    } catch (error) {
      captureException(error, { tags: { feature: 'languageContext' }, extra: { action: 'syncLanguageToFirestore' } });
      console.warn('Failed to sync language to Firestore:', error);
      // Continue using localStorage even if Firestore update fails
    } finally {
      setIsUpdatingLanguage(false);
    }
  };

  const changeLanguage = async (language: string) => {
    if (!isValidLanguage(language)) {
      console.warn(`Invalid language code: ${language}. Using 'es' instead.`);
      language = 'es';
    }

    // Update i18next and local state immediately for responsive UI
    i18n.changeLanguage(language);
    setCurrentLanguage(language);
    localStorage.setItem('i18nextLng', language);

    // TODO: save language settings to user data in firestore:
    const idToken = await user.getIdToken();
    await axios.put(
      `${process.env.REACT_APP_API_URL || ''}/api/user/settings`,
      {
        language: language,
        username: userData?.username,
      },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    // Sync to Firestore in the background (non-blocking)
    syncLanguageToFirestore(language);
  };

  // Initialize language on component mount and when user data changes
  useEffect(() => {
    const initializeLanguage = () => {
      const preferredLanguage = getUserLanguagePreference(userData?.language);
      
      // Check if we need to update localStorage based on Firestore data
      const storedLanguage = localStorage.getItem('i18nextLng');
      
      if (userData?.language && userData.language !== storedLanguage && userData.language !== currentLanguage) {
        // Firestore has different language than localStorage, update localStorage
        console.log('Syncing language from Firestore to localStorage:', userData.language);
        changeLanguage(userData.language);
      } else if (preferredLanguage !== currentLanguage && preferredLanguage !== storedLanguage) {
        // Use preferred language if different from current
        changeLanguage(preferredLanguage);
      }
    };

    initializeLanguage();
  }, [userData?.language, user]); // Re-run when user data or authentication changes

  // Update current language when i18n language changes
  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  const value: LanguageContextType = {
    currentLanguage,
    changeLanguage,
    availableLanguages,
    isUpdatingLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 